// The control settings the Connect controls panel produces, as pure functions.
//
// WHY THIS EXISTS. This logic used to live only inside two `useMount` effects —
// one in `panel.tsx` (pick the protocol) and one in `visca-ip.tsx` (fill in the
// endpoint) — so the ONLY way to obtain a working control configuration was to
// render that panel. The first-run funnel replaces that panel with its own
// connection test, reimplemented the resolution from the model template, and
// got it wrong: it sent `http://192.168.1.221:0` for a camera whose working
// endpoint is `visca://192.168.1.221:1259?headers=false`. Nothing listens on
// port 0, so every control check failed (measured live 2026-08-28).
//
// Two rules were missing from that reimplementation, and both are here now:
//
//   1. An ONVIF template means VISCA/IP. The app does not speak ONVIF for
//      control, and the panel has always silently substituted. A discovery's
//      own `onvif://host:81` is not a control endpoint.
//   2. The endpoint is layered, not picked. Template first for port and header
//      mode, then the VIDEO configuration's host — which is the address the
//      Configure video step already proved reachable — then the app defaults.
//   3. EVERY protocol has defaults, not just VISCA/IP. A template naming HTTP
//      carries no port, and `HttpDefaults` is the only thing that supplies 80 —
//      which is why the first fix here still shipped `http://host:0`.
//
// The panel keeps calling these, so there is one implementation and the funnel
// cannot drift from it again. That is the whole point: the funnel's own copies
// of shared behaviour are what this PR exists to delete.
//
// Per rules: ai-code-requires-tests (normalize.test.ts), code-comments-required,
// reference-rules-in-comments, investigate-before-adding, think-in-systems
// (ai.knowledge/global).

import type { Settings } from '@studio/models/control';
import { ControlDefaults, Protocol } from '@studio/models/control';
import { HttpDefaults } from '@studio/models/control/http';
import { OnvifDefaults } from '@studio/models/control/onvif';
import { ViscaIpDefaults } from '@studio/models/control/visca-ip';
import { ViscaSerialDefaults } from '@studio/models/control/visca-serial';
import { defaultsDeep } from 'lodash-es';
import type { Template } from '#~/device/registry/index.js';

/**
 * The app's own defaults per protocol — the same constant each settings
 * component layers in. VISCA/serial is the one protocol with no network
 * address, so it does not take the camera's host.
 */
const PROTOCOL_DEFAULTS: Readonly<
  Record<Protocol, { readonly defaults: object; readonly addressed: boolean }>
> = Object.freeze({
  [Protocol.VISCA_IP]: { defaults: ViscaIpDefaults, addressed: true },
  [Protocol.HTTP]: { defaults: HttpDefaults, addressed: true },
  [Protocol.ONVIF]: { defaults: OnvifDefaults, addressed: true },
  [Protocol.VISCA_SERIAL]: { defaults: ViscaSerialDefaults, addressed: false }
});

/** The control block a registry template carries, if any. */
type TemplateControl = { readonly protocol?: Protocol | undefined } | undefined;

/**
 * The candidate's control block as the callers hold it: an immer draft in the
 * panel, a plain object in the funnel. Only `protocol` is read, and it is
 * optional in both — hence `| undefined` explicitly, which
 * `exactOptionalPropertyTypes` requires.
 */
type ControlLike = { readonly protocol?: Protocol | undefined } | undefined;

const templateControl = (template: Template | undefined): TemplateControl =>
  (template as { control?: TemplateControl } | undefined)?.control;

/**
 * Which control protocol to configure, given the candidate's current one and
 * the chosen model's template.
 *
 * ONVIF becomes VISCA/IP because the app does not support ONVIF for control —
 * moved verbatim from `panel.tsx`'s mount effect, which is the only place this
 * substitution has ever lived.
 */
export const resolveControlProtocol = (
  control: ControlLike,
  template: Template | undefined
): Protocol | undefined => {
  const suggested = templateControl(template)?.protocol;

  if (suggested === undefined) return control?.protocol;

  return suggested === Protocol.ONVIF ? Protocol.VISCA_IP : suggested;
};

/**
 * Fill in a VISCA/IP endpoint: template, then the camera's own address, then
 * the app defaults. Moved verbatim from `visca-ip.tsx`'s mount effect.
 *
 * The template only contributes when it IS a VISCA/IP template — applying a
 * template's port across protocols is how control reached a dead endpoint.
 */
export const resolveProtocolSettings = (
  protocol: Protocol,
  control: object | undefined,
  template: Template | undefined,
  video: unknown
): Settings => {
  const { defaults, addressed } = PROTOCOL_DEFAULTS[protocol];

  return defaultsDeep(
    { ...control, protocol },
    templateControl(template)?.protocol === protocol ? templateControl(template) : {},
    addressed && typeof video === 'object' && video !== null && 'host' in video
      ? { host: (video as { host?: unknown }).host }
      : {},
    defaults,
    ControlDefaults
  ) as Settings;
};

/** VISCA/IP, kept as its own name because `visca-ip.tsx` reads better calling it. */
export const resolveViscaIpSettings = (
  control: object | undefined,
  template: Template | undefined,
  video: unknown
): Settings => resolveProtocolSettings(Protocol.VISCA_IP, control, template, video);

/**
 * The complete control settings the panel would produce, without rendering it.
 *
 * Returns undefined when there is no protocol to configure at all, which is the
 * honest "this camera has nothing to talk to" answer — the connection test then
 * hides its control row rather than failing a check against an invented
 * address.
 */
export const resolveControlSettings = (
  control: ControlLike,
  template: Template | undefined,
  video: unknown
): object | undefined => {
  const protocol = resolveControlProtocol(control, template);

  if (protocol === undefined) return undefined;

  // A protocol CHANGE discards the previous protocol's settings, exactly as
  // `content.tsx` handleProtocolChange does (`{ ...cache[next], protocol: next }`).
  // Carrying them over is not a merge, it is a category error: an ONVIF
  // discovery's port 81 surviving into a VISCA/IP config produces
  // `visca://host:81`, and `defaultsDeep` would then refuse to fill in the
  // template's real port because the key is already set. The model step clears
  // control for this same reason (`manufacturer/details.tsx`).
  const carried = control?.protocol === protocol ? control : undefined;

  // EVERY protocol gets its defaults, not just VISCA/IP. Returning the bare
  // `{ protocol }` for the others is what produced `http://192.168.1.221:0`
  // for a model whose template names HTTP without a port: `HttpDefaults`
  // supplies 80, and nothing else does (measured live 2026-08-28).
  return resolveProtocolSettings(protocol, carried, template, video);
};
